import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiActivity, FiAlertOctagon, FiAlertTriangle, FiCheckCircle, FiClock, FiEdit2, FiHelpCircle, FiMinusCircle, FiMoreHorizontal,
  FiPlus, FiPower, FiSliders, FiTrash2, FiX, FiXCircle, FiZap,
} from "react-icons/fi";

import {
  brasOverview, createRouter, deleteProbeTarget, deleteRouter, routerHistory, routerPing, routerTrace, saveProbePolicy, saveProbeTarget,
  targetHistory, testRouter, updateRouter,
  type BRASOverview, type BRASRouter, type Finding, type Hop, type Pair, type PairState, type PingResult, type ProbePoint,
  type ProbePolicy, type ProbeTarget, type ProbeTrace, type RouterForm, type TargetForm,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { Alert } from "@/lib/types";
import {
  groups, key, lossText, MAX_COLS, msText, overall, pppoeDrop, probes, routerInternet, spread, STATE_RANK, summary, uptimeText,
  visibleRouters, type Overall,
} from "@/lib/bras";
import { useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import { PingStats } from "@/components/CustomerPing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import { usePref } from "@/lib/prefs";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;

// ---------- vocabulary ----------

type Tone = "good" | "warn" | "crit" | "muted" | "info";
const TONE: Record<Tone, { text: string; bg: string; dot: string; border: string }> = {
  good: { text: "text-signal-good", bg: "bg-signal-good/10", dot: "bg-signal-good", border: "border-signal-good/40" },
  warn: { text: "text-signal-warn", bg: "bg-amber-400/10", dot: "bg-signal-warn", border: "border-amber-400/40" },
  crit: { text: "text-signal-crit", bg: "bg-signal-crit/10", dot: "bg-signal-crit", border: "border-signal-crit/50" },
  muted: { text: "text-slate-400", bg: "bg-slate-700/40", dot: "bg-slate-500", border: "border-rule" },
  info: { text: "text-signal", bg: "bg-signal/10", dot: "bg-signal", border: "border-signal/40" },
};
const STATE_UI: Record<PairState, { tone: Tone; icon: IconType }> = {
  healthy: { tone: "good", icon: FiCheckCircle }, degraded: { tone: "warn", icon: FiAlertTriangle },
  loss: { tone: "crit", icon: FiAlertTriangle }, unreachable: { tone: "crit", icon: FiXCircle }, unknown: { tone: "muted", icon: FiHelpCircle },
};
const OVERALL_UI: Record<Overall, { tone: Tone; icon: IconType }> = {
  healthy: { tone: "good", icon: FiCheckCircle }, degraded: { tone: "warn", icon: FiAlertTriangle }, outage: { tone: "crit", icon: FiAlertOctagon },
  stale: { tone: "warn", icon: FiClock }, none: { tone: "muted", icon: FiHelpCircle },
};
/** Line colours for up to eight BRAS in the comparison chart. */
const LINE = ["#22d3ee", "#a78bfa", "#f59e0b", "#34d399", "#f472b6", "#60a5fa", "#fb7185", "#facc15"];

function Badge({ tone, icon: I, children, className }: { tone: Tone; icon?: IconType; children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase", TONE[tone].text, TONE[tone].bg, className)}>
      {I && <I className="size-3" />} {children}
    </span>
  );
}
const Dot = ({ tone, className }: { tone: Tone; className?: string }) => <span className={cn("inline-block size-2 shrink-0 rounded-full", TONE[tone].dot, className)} />;
const ago = (iso: string | null | undefined, now: number) => (iso ? durText(Math.max(0, now - Date.parse(iso))) : "—");

function Panel({ title, right, children, className, pad = true }: { title: ReactNode; right?: ReactNode; children: ReactNode; className?: string; pad?: boolean }) {
  return (
    <section className={cn("rounded-lg border border-rule bg-panel", className)}>
      <header className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-rule px-4 py-1.5">
        <h2 className="eyebrow text-slate-300">{title}</h2>{right}
      </header>
      <div className={pad ? "p-4" : undefined}>{children}</div>
    </section>
  );
}

/** Finding case -> alert key, as the collector raises them. */
const findingAlert = (f: Finding): [string, number, string] =>
  f.case === "shared" ? ["internet", 0, "internet.degraded"]
    : f.case === "bras" ? ["router", f.router_ids[0], "bras.internet"]
    : f.case === "target" ? ["probe", f.target_ids[0], "probe.degraded"]
    : ["probe", f.target_ids[0], "probe.path"];

// ---------- page ----------

type Drawer = { kind: "target"; id: number } | { kind: "router"; id: number } | { kind: "result"; tr: ProbeTrace } | null;

export default function BRASPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const canTest = admin || user?.role === "noc";
  const now = useNow(5_000);
  const q = useQuery({ queryKey: ["bras"], queryFn: brasOverview, refetchInterval: 20_000 });
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [editRouter, setEditRouter] = useState<BRASRouter | "new" | null>(null);
  const [editTarget, setEditTarget] = useState<ProbeTarget | "new" | null>(null);
  const [delRouter, setDelRouter] = useState<BRASRouter | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [tool, setTool] = useState<{ router?: number; target?: string; run?: "ping" | "mtr"; nonce: number }>({ nonce: 0 });
  const [picked, setPicked] = usePref<number[]>("bras.compare", [], "nocops.bras.compare");
  const toolRef = useRef<HTMLDivElement>(null);

  if (q.isLoading) return <p className="text-slate-400">{t("common.loading")}</p>;
  if (q.error) return <p className="text-signal-crit">{(q.error as Error).message}</p>;
  const d = q.data!;
  const o = overall(d, now);
  const cols = visibleRouters(d.routers.filter((r) => r.enabled), picked);
  const runTool = (router: number, target: string, run: "ping" | "mtr") => {
    setDrawer(null);
    setTool({ router, target, run, nonce: Date.now() });
    setTimeout(() => toolRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  const routerName = (id: number) => d.routers.find((r) => r.id === id)?.name ?? `#${id}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("bras.title")}</h1>
          <p className="mt-0.5 text-[12.5px] text-slate-400">{t("bras.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {admin && <Button size="sm" variant="ghost" onClick={() => setPolicyOpen(true)}><FiSliders /> {t("bras.thresholds")}</Button>}
          {admin && <Button size="sm" variant="outline" onClick={() => setEditTarget("new")}><FiPlus /> {t("bras.addTarget")}</Button>}
          {admin && <Button size="sm" onClick={() => setEditRouter("new")}><FiPlus /> {t("bras.addRouter")}</Button>}
        </div>
      </div>

      {d.routers.length === 0 ? <SetupHelp admin={admin} onAdd={() => setEditRouter("new")} /> : (
        <>
          <HealthStrip d={d} o={o} now={now} onRun={runTool} onTarget={(id) => setDrawer({ kind: "target", id })} canTest={canTest} />

          <div className={cn("grid gap-3", d.routers.length > 3 ? "[grid-template-columns:repeat(auto-fit,minmax(250px,1fr))]" : "[grid-template-columns:repeat(auto-fit,minmax(340px,1fr))]")}>
            {d.routers.map((r) => (
              <RouterCard key={r.id} r={r} d={d} now={now} admin={admin} compact={d.routers.length > 3}
                onOpen={() => setDrawer({ kind: "router", id: r.id })} onEdit={() => setEditRouter(r)} onDelete={() => setDelRouter(r)} />
            ))}
          </div>

          <Matrix d={d} cols={cols} now={now} admin={admin} setPicked={setPicked}
            onTarget={(id) => setDrawer({ kind: "target", id })} onEditTarget={setEditTarget} />

          <div ref={toolRef} className="grid gap-3 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {canTest ? <Tools d={d} preset={tool} onResult={(tr) => setDrawer({ kind: "result", tr })} /> : <div />}
            <RecentDiagnostics d={d} now={now} locale={locale} routerName={routerName} onOpen={(tr) => setDrawer({ kind: "result", tr })} />
          </div>
        </>
      )}

      {drawer?.kind === "target" && <TargetDrawer d={d} id={drawer.id} canTest={canTest} onClose={() => setDrawer(null)} onRun={runTool} />}
      {drawer?.kind === "router" && <RouterDrawer d={d} id={drawer.id} now={now} canTest={canTest} onClose={() => setDrawer(null)}
        onRun={(rid) => runTool(rid, "", "ping")} onTarget={(id) => setDrawer({ kind: "target", id })} />}
      {drawer?.kind === "result" && <ResultDrawer tr={drawer.tr} router={routerName(drawer.tr.router_id)} onClose={() => setDrawer(null)} />}
      {editRouter && <RouterDialog edit={editRouter === "new" ? null : editRouter} onClose={() => setEditRouter(null)} />}
      {editTarget && <TargetDialog edit={editTarget === "new" ? null : editTarget} routers={d.routers} targets={d.targets} onClose={() => setEditTarget(null)} />}
      {delRouter && <DeleteRouter r={delRouter} onClose={() => setDelRouter(null)} />}
      {policyOpen && <PolicyDialog policy={d.policy} onClose={() => setPolicyOpen(false)} />}
    </div>
  );
}

// ---------- health strip ----------

function caseText(f: Finding, d: BRASOverview, t: T) {
  const rn = f.router_ids.map((id) => d.routers.find((r) => r.id === id)?.name ?? `#${id}`).join(", ");
  const tn = f.target_ids.map((id) => d.targets.find((x) => x.id === id)?.name ?? `#${id}`);
  const list = tn.length > 3 ? `${tn.slice(0, 3).join(", ")} +${tn.length - 3}` : tn.join(", ");
  switch (f.case) {
    case "shared": return { title: t("bras.case.shared"), detail: t("bras.case.sharedD", { n: tn.length, list }) };
    case "bras": return { title: t("bras.case.bras", { bras: rn }), detail: t("bras.case.brasD", { n: tn.length, list }) };
    case "target": return { title: t("bras.case.target", { target: list }), detail: t("bras.case.targetD") };
    default: return { title: t("bras.case.path", { target: list, bras: rn }), detail: t("bras.case.pathD") };
  }
}

function HealthStrip({ d, o, now, onRun, onTarget, canTest }: {
  d: BRASOverview; o: ReturnType<typeof overall>; now: number; canTest: boolean;
  onRun: (router: number, target: string, run: "ping" | "mtr") => void; onTarget: (id: number) => void;
}) {
  const { t, locale } = useI18n();
  const ui = OVERALL_UI[o.state];
  const findings = d.analysis.findings ?? [];
  const incidentFor = (tt: string, id: number, metric: string) => d.incidents.find((a) => a.target_type === tt && a.target_id === id && a.metric === metric);
  const routerIncidents = d.incidents.filter((a) => a.metric === "bras.unreachable" || a.metric === "bras.pppoe_drop");
  const newest = d.routers.reduce<string | null>((m, r) => (r.last_polled_at && (!m || r.last_polled_at > m) ? r.last_polled_at : m), null);
  return (
    <section className={cn("rounded-lg border bg-panel", o.state === "healthy" ? "border-rule" : TONE[ui.tone].border)}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <ui.icon className={cn("size-6", TONE[ui.tone].text)} />
          <div>
            <p className="eyebrow">{t("bras.health")}</p>
            <p className={cn("text-[17px] font-semibold tracking-[0.06em] uppercase", TONE[ui.tone].text)}>{t(`bras.ov.${o.state}` as Key)}</p>
          </div>
        </div>
        <dl className="data flex flex-wrap items-baseline gap-x-5 gap-y-1 text-[12.5px]">
          <Fig label={t("bras.f.bras")} value={`${o.up}/${o.routers}`} tone={o.up < o.routers ? "crit" : undefined} />
          <Fig label={t("bras.f.targets")} value={`${o.healthy}/${o.targets}`} tone={o.healthy < o.targets ? "warn" : undefined} />
          <Fig label={t("bras.f.loss")} value={fmtNum(locale, o.withLoss)} tone={o.withLoss ? "warn" : undefined} />
          <Fig label={t("bras.f.median")} value={msText(o.medianMs)} />
        </dl>
        <span className="flex-1" />
        <p className={cn("flex items-center gap-2 text-[12px]", o.state === "stale" ? "text-signal-warn" : "text-slate-400")}>
          {o.state === "stale" ? <FiClock /> : <span className="beacon size-2 rounded-full bg-signal-good text-signal-good" />}
          {o.state === "stale" ? t("bras.staleNote", { age: ago(d.last_probe_at, now) }) : t("bras.live")}
          <span className="text-slate-500">· {t("bras.pollAge", { age: ago(newest, now) })} · {t("bras.probeAge", { age: ago(d.last_probe_at, now) })}</span>
        </p>
      </div>
      {(findings.length > 0 || routerIncidents.length > 0) && (
        <ul className="divide-y divide-rule/60 border-t border-rule">
          {routerIncidents.map((a) => <RouterIncident key={a.id} a={a} d={d} now={now} />)}
          {findings.map((f, i) => {
            const [tt, id, metric] = findingAlert(f);
            const inc = incidentFor(tt, id, metric);
            const c = caseText(f, d, t);
            const tone: Tone = f.severity === "critical" ? "crit" : "warn";
            const tgt = d.targets.find((x) => x.id === f.target_ids[0]);
            return (
              <li key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-[12.5px]">
                <FiAlertTriangle className={cn("size-4 shrink-0", TONE[tone].text)} />
                <span className={cn("font-semibold", TONE[tone].text)}>{c.title}</span>
                <span className="text-slate-400">{c.detail}</span>
                {inc && <span className="text-slate-500">{t("bras.since", { age: ago(inc.opened_at, now) })}</span>}
                <span className="ml-auto flex items-center gap-3">
                  {f.target_ids.length === 1 && <button type="button" onClick={() => onTarget(f.target_ids[0])} className="text-slate-300 hover:text-signal">{t("bras.details")}</button>}
                  {canTest && tgt && <button type="button" onClick={() => onRun(f.router_ids[0], tgt.address, "mtr")} className="text-slate-300 hover:text-signal">{t("bras.runMtr")}</button>}
                  {inc && <Link to={`/alerts?open=${tt}:${id}`} className="text-signal hover:underline">{t("bras.openIncident")}</Link>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RouterIncident({ a, d, now }: { a: Alert; d: BRASOverview; now: number }) {
  const { t } = useI18n();
  const r = d.routers.find((x) => x.id === a.target_id);
  const drop = a.metric === "bras.pppoe_drop";
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 bg-signal-crit/[0.05] px-4 py-2 text-[12.5px]">
      <FiAlertOctagon className="size-4 shrink-0 text-signal-crit" />
      <span className="font-semibold text-signal-crit">{t(drop ? "bras.inc.pppDrop" : "bras.inc.down", { bras: r?.name ?? "" })}</span>
      <span className="text-slate-300">{a.message}</span>
      <span className="text-slate-500">{t("bras.since", { age: ago(a.opened_at, now) })}</span>
      <Link to={`/alerts?open=router:${a.target_id}`} className="ml-auto text-signal hover:underline">{t("bras.openIncident")}</Link>
    </li>
  );
}

const Fig = ({ label, value, tone }: { label: string; value: string; tone?: Tone }) => (
  <div className="flex items-baseline gap-1.5"><dd className={cn("text-[15px] font-semibold", tone ? TONE[tone].text : "text-slate-100")}>{value}</dd><dt className="text-[11.5px] text-slate-500">{label}</dt></div>
);

// ---------- BRAS cards ----------

function RouterCard({ r, d, now, admin, compact, onOpen, onEdit, onDelete }: {
  r: BRASRouter; d: BRASOverview; now: number; admin: boolean; compact?: boolean; onOpen: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const toggle = useMutation({ mutationFn: () => updateRouter(r.id, { enabled: !r.enabled }), onSuccess: () => qc.invalidateQueries({ queryKey: ["bras"] }) });
  const net = routerInternet(d, r);
  const drop = pppoeDrop(r.pppoe_series, d.policy);
  const down = r.enabled && r.status === "down";
  const tone: Tone = !r.enabled ? "muted" : down ? "crit" : r.status === "up" ? "good" : "muted";
  const mem = r.mem_used_b !== null && r.mem_total_b ? (r.mem_used_b / r.mem_total_b) * 100 : null;
  const resTone = (v: number | null) => (v === null ? "text-slate-500" : v >= 85 ? "text-signal-crit" : v >= 70 ? "text-signal-warn" : "text-slate-400");
  const netTone = STATE_UI[net.worst].tone;
  return (
    <article className={cn("flex flex-col rounded-lg border bg-panel", down || drop ? TONE.crit.border
      : net.worst === "loss" || net.worst === "unreachable" ? TONE.crit.border : net.worst === "degraded" ? TONE.warn.border : "border-rule", !r.enabled && "opacity-60")}>
      <div className="flex cursor-pointer items-start gap-3 px-4 pt-3" onClick={onOpen}>
        <Dot tone={tone} className="mt-1.5" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[13.5px] font-semibold tracking-wide text-slate-100 uppercase hover:text-signal">{r.name}</h3>
          {!compact && <p className="truncate text-[11px] text-slate-500">{[r.kind === "routeros" ? `RouterOS ${r.version ?? ""}`.trim() : "Linux", r.board, r.host].filter(Boolean).join(" · ")}</p>}
        </div>
        <span onClick={(e) => e.stopPropagation()}>
          {admin && <Menu label={r.name} items={[
            { icon: FiEdit2, label: t("common.edit"), onClick: onEdit },
            { icon: FiPower, label: t(r.enabled ? "bras.disable" : "bras.enable"), onClick: () => toggle.mutate() },
            { icon: FiTrash2, label: t("common.delete"), danger: true, onClick: onDelete },
          ]} />}
        </span>
      </div>
      {down ? (
        <div className="mx-4 mt-2 rounded-sm border border-signal-crit/40 bg-signal-crit/[0.06] px-3 py-2 text-[12px]">
          <p className="font-semibold tracking-[0.1em] text-signal-crit uppercase">{t("bras.unreachable")}</p>
          <p className="mt-0.5 line-clamp-2 text-slate-300" title={r.last_error ?? ""}>{r.last_error}</p>
          <p className="text-[11px] text-slate-500">{t("bras.lastOk", { age: ago(r.last_ok_at, now) })}</p>
        </div>
      ) : (
        <button type="button" onClick={onOpen} className="grid grid-cols-[auto_1fr] items-end gap-x-4 px-4 pt-2 text-left">
          <div>
            <p className="text-[11px] text-slate-500">PPPoE</p>
            <p className={cn("data leading-none font-semibold", compact ? "text-[20px]" : "text-[24px]", drop ? "text-signal-crit" : "text-slate-100")}>
              {r.pppoe_sessions === null ? "—" : fmtNum(locale, r.pppoe_sessions)}
            </p>
            {r.pppoe_delta_1h !== undefined && (
              <p className={cn("data mt-0.5 text-[11px]", r.pppoe_delta_1h < 0 ? "text-signal-warn" : "text-slate-500")}>
                {t("bras.delta1h", { d: `${r.pppoe_delta_1h > 0 ? "+" : r.pppoe_delta_1h < 0 ? "−" : "±"}${Math.abs(r.pppoe_delta_1h)}` })}
              </p>
            )}
          </div>
          <MiniLine points={(r.pppoe_series ?? []).map((p) => ({ ts: p.ts, v: p.n }))} className={compact ? "h-7" : "h-10"} tone={drop ? "crit" : "info"} />
        </button>
      )}
      {drop && !down && (
        <p className="mx-4 mt-2 flex items-center gap-2 rounded-sm bg-signal-crit/[0.08] px-2 py-1 text-[12px] text-signal-crit">
          <FiAlertOctagon className="size-3.5" /> <b>{t("bras.pppDrop")}</b> {drop.peak} → {drop.cur} · {t("bras.pppDropN", { n: drop.drop })}
        </p>
      )}
      {!down && <div className="mt-2 flex items-center gap-2 overflow-hidden px-4 text-[12px] whitespace-nowrap">
        <span className="text-slate-500">{t("bras.internetShort")}</span>
        <span className={cn("flex items-center gap-1.5", TONE[netTone].text)}><Dot tone={netTone} /> {net.total ? t("bras.nHealthy", { n: net.healthy, total: net.total }) : t("bras.noTargetsShort")}</span>
        {net.medianMs !== null && <span className="data text-slate-400">· {msText(net.medianMs)}</span>}
        {net.loss !== null && net.loss > 0 && <span className="data text-signal-warn">· {lossText(net.loss)}</span>}
      </div>}
      <p className={cn("data mt-auto flex flex-wrap gap-x-3 border-t border-rule/60 px-4 pt-2 pb-2.5 text-[11px]", down && "opacity-50")} title={down ? t("bras.lastKnown") : undefined}>
        <span className={resTone(r.cpu_pct)}>{r.kind === "linux" ? t("bras.load") : "CPU"} {r.cpu_pct === null ? "—" : `${r.cpu_pct.toFixed(0)}%`}</span>
        <span className={resTone(mem)}>MEM {mem === null ? "—" : `${mem.toFixed(0)}%`}</span>
        <span className="text-slate-500">{t("bras.up")} {uptimeText(r.uptime_s)}</span>
        <span className="ml-auto text-slate-500">{t("bras.pollAge", { age: ago(r.last_polled_at, now) })}</span>
      </p>
    </article>
  );
}

// ---------- comparison matrix ----------

function Matrix({ d, cols, now, admin, setPicked, onTarget, onEditTarget }: {
  d: BRASOverview; cols: BRASRouter[]; now: number; admin: boolean; setPicked: (p: number[]) => void;
  onTarget: (id: number) => void; onEditTarget: (t: ProbeTarget) => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const enabled = d.routers.filter((r) => r.enabled);
  const many = enabled.length > MAX_COLS;
  const showDelta = cols.length >= 2;
  const grid = `minmax(190px,1.1fr) repeat(${cols.length}, minmax(260px,2fr))${showDelta ? " minmax(150px,0.8fr)" : ""}${admin ? " 36px" : ""}`;
  if (!d.targets.length) {
    return <Panel title={t("bras.internet")}><p className="text-[12.5px] text-slate-500">{t(admin ? "bras.noTargetsAdmin" : "bras.noTargets")}</p></Panel>;
  }
  return (
    <Panel pad={false} title={t("bras.internet")} right={
      <div className="flex items-center gap-3 text-[11.5px] text-slate-500">
        {many && (
          <span className="flex flex-wrap items-center gap-1">
            {t("bras.compare")}
            {enabled.map((r) => {
              const on = cols.some((c) => c.id === r.id);
              return (
                <button key={r.id} type="button" aria-pressed={on}
                  onClick={() => setPicked(on ? cols.filter((c) => c.id !== r.id).map((c) => c.id) : [...cols.map((c) => c.id), r.id].slice(-MAX_COLS))}
                  className={cn("rounded-sm border px-1.5 py-0.5", on ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400")}>{r.name}</button>
              );
            })}
          </span>
        )}
        <span>{t("bras.every", { s: d.interval_s })}</span>
      </div>
    }>
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          <div className="grid border-b border-rule px-4 py-2" style={{ gridTemplateColumns: grid }}>
            <span className="eyebrow">{t("bras.target")}</span>
            {cols.map((r) => <span key={r.id} className="eyebrow truncate pr-3">{r.name}</span>)}
            {showDelta && <span className="eyebrow">{t("bras.diff")}</span>}
            {admin && <span />}
          </div>
          {groups(d.targets).map((g) => (
            <div key={g.category || "_"}>
              {g.category && <p className="eyebrow border-b border-rule/60 bg-white/[0.015] px-4 py-0.5 text-[10px]">{g.category}</p>}
              {g.targets.map((tg) => {
                const cells = cols.map((r) => ({ router: r, pair: probes(tg, r.id) ? d.analysis.pairs[key(r.id, tg.id)] : undefined }));
                const sp = spread(cells, d.policy);
                const worst = cells.reduce<PairState>((w, c) => (c.pair && STATE_RANK[c.pair.state] < STATE_RANK[w] ? c.pair.state : w), "healthy");
                return (
                  <div key={tg.id} className={cn("grid items-center border-b border-rule/50 px-4 py-1 last:border-b-0 hover:bg-white/[0.015]", !tg.enabled && "opacity-50",
                    worst === "loss" || worst === "unreachable" ? "bg-signal-crit/[0.03]" : worst === "degraded" ? "bg-amber-400/[0.025]" : "")}
                    style={{ gridTemplateColumns: grid }}>
                    <button type="button" onClick={() => onTarget(tg.id)} className="min-w-0 pr-3 text-left">
                      <span className="block truncate text-[13px] font-medium text-slate-100 hover:text-signal">{tg.name}</span>
                      <span className="data block truncate text-[11px] text-slate-500">{tg.address}</span>
                    </button>
                    {cells.map(({ router, pair }) => (
                      <Cell key={router.id} pair={pair} probed={probes(tg, router.id)} points={d.series[key(router.id, tg.id)]} now={now} onOpen={() => onTarget(tg.id)} />
                    ))}
                    {showDelta && (
                      <span className="text-[12px]">
                        {!sp ? <span className="text-slate-600">—</span> : sp.differentServers && sp.delta >= d.policy.dev_min_ms
                          ? <span className="text-signal" title={t("bras.diffServersHint")}>{t("bras.diffServers")} <span className="data text-slate-500">Δ {sp.delta.toFixed(1)} ms</span></span>
                          : sp.meaningful
                          ? <span className="font-semibold text-signal-warn">⚠ +{sp.delta.toFixed(1)} ms <span className="font-normal">{t("bras.via", { bras: sp.slow.name })}</span></span>
                          : <span className="data text-slate-500">Δ {sp.delta.toFixed(1)} ms</span>}
                      </span>
                    )}
                    {admin && (
                      <span className="text-right">
                        <Menu label={tg.name} items={[
                          { icon: FiEdit2, label: t("common.edit"), onClick: () => onEditTarget(tg) },
                          { icon: FiPower, label: t(tg.enabled ? "bras.disable" : "bras.enable"), onClick: () => void saveProbeTarget({ enabled: !tg.enabled }, tg.id).then(() => qc.invalidateQueries({ queryKey: ["bras"] })) },
                          { icon: FiTrash2, label: t("common.delete"), danger: true, onClick: () => { if (confirm(t("bras.delTarget", { name: tg.name }))) void deleteProbeTarget(tg.id).then(() => qc.invalidateQueries({ queryKey: ["bras"] })); } },
                        ]} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          <p className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-rule px-4 py-1.5 text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5"><span className="h-px w-4 bg-signal" /> {t("bras.lg.latency")}</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-1 bg-signal-crit/80" /> {t("bras.lg.loss")}</span>
            <span className="flex items-center gap-1.5"><span className="h-1.5 w-1 bg-slate-500" /> {t("bras.lg.error")}</span>
            <span>{t("bras.lg.judged", { pct: d.policy.dev_pct, ms: d.policy.dev_min_ms })}</span>
            <span>{t("bras.lg.servers")}</span>
          </p>
        </div>
      </div>
    </Panel>
  );
}

function Cell({ pair, probed, points, now, onOpen }: { pair: Pair | undefined; probed: boolean; points: ProbePoint[] | undefined; now: number; onOpen: () => void }) {
  const { t } = useI18n();
  if (!probed) return <span className="pr-3 text-[12px] text-slate-600">{t("bras.notProbed")}</span>;
  const state: PairState = pair?.state ?? "unknown";
  const ui = STATE_UI[state];
  const lossNow = pair?.loss_pct ?? null;
  const win = pair?.window_loss ?? null;
  return (
    <button type="button" onClick={onOpen} className="grid min-w-0 grid-cols-[168px_1fr] items-center gap-2 pr-3 text-left">
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <Dot tone={ui.tone} />
          {state === "unreachable" ? <span className="text-[12px] font-semibold tracking-[0.08em] text-signal-crit uppercase">{t("bras.st.unreachable")}</span>
            : state === "unknown" ? <span className="text-[12px] text-slate-400">{t(`bras.why.${pair?.reason || "no_data"}` as Key)}</span>
            : <span className={cn("data text-[13.5px]", state === "degraded" ? "text-signal-warn" : "text-slate-100")}>{msText(pair?.median_ms)}</span>}
        </span>
        <span className="block truncate pl-3.5 text-[11px]">
          {state === "loss" && lossNow !== null ? <b className="font-semibold tracking-wide text-signal-crit uppercase">{lossText(lossNow)} {t("bras.lossUp")}</b>
            : state === "degraded" && pair?.baseline_ms ? <span className="text-signal-warn">{t("bras.vsTypical", { d: ((pair.median_ms ?? 0) - pair.baseline_ms).toFixed(1), v: msText(pair.baseline_ms) })}</span>
            : state === "unknown" ? <span className="text-slate-600">{pair?.at ? t("bras.lastAt", { age: ago(pair.at, now) }) : ""}</span>
            : <span className={win ? "text-signal-warn" : "text-slate-500"}>{t("bras.loss3h", { n: win === null ? "—" : lossText(win) })}</span>}
          {(pair?.ips?.length ?? 0) > 1 && (
            <span className="ml-1.5 text-signal" title={pair!.ips!.map((x) => `${x.ip}${x.name ? ` (${x.name})` : ""}: ${msText(x.median_ms)} · ${Math.round(x.share * 100)}%`).join("\n")}>
              · {t("bras.nServers", { n: pair!.ips!.length })}
            </span>
          )}
        </span>
      </span>
      <Spark points={points ?? []} className="h-8" />
    </button>
  );
}

/**
 * Latency over the window with loss events as red bars (height = loss %) and
 * probes that could not run as grey ticks. Hover: the numbers behind it.
 */
function Spark({ points, className, period = "3h" }: { points: ProbePoint[]; className?: string; period?: string }) {
  const { t, locale } = useI18n();
  const [hover, setHover] = useState<number | null>(null);
  const W = 200, H = 32;
  if (points.length < 2) return <span className={cn("block", className)} />;
  const t0 = Date.parse(points[0].ts), t1 = Math.max(t0 + 1, Date.parse(points[points.length - 1].ts));
  const vals = points.map((p) => p.avg).filter((v): v is number => v !== null);
  const lo = vals.length ? Math.min(...vals) : 0, hi = vals.length ? Math.max(...vals) : 1;
  const pad = Math.max(0.5, (hi - lo) * 0.15);
  const x = (ts: string) => ((Date.parse(ts) - t0) / (t1 - t0)) * W;
  const y = (v: number) => H - 2 - ((v - (lo - pad)) / (hi - lo + 2 * pad)) * (H - 4);
  // One line per server: a hostname alternating between CDN nodes draws as
  // two steady lines, not as a zig-zag that looks like jitter.
  const byIp = new Map<string, number>();
  for (const p of points) if (p.avg !== null) byIp.set(p.ip ?? "", (byIp.get(p.ip ?? "") ?? 0) + 1);
  const ips = [...byIp.entries()].sort((a, b) => b[1] - a[1]).map(([ip]) => ip);
  const segs: { d: string; i: number }[] = [];
  ips.forEach((ip, i) => {
    let cur = "";
    for (const p of points) {
      if ((p.ip ?? "") !== ip) continue;
      if (p.avg === null) { if (cur) segs.push({ d: cur, i }); cur = ""; continue; }
      cur += `${cur ? "L" : "M"}${x(p.ts).toFixed(1)},${y(p.avg).toFixed(1)}`;
    }
    if (cur) segs.push({ d: cur, i });
  });
  const s = summary(points);
  const hp = hover === null ? null : points[hover];
  return (
    <span className={cn("relative block min-w-0", className)}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const tx = t0 + ((e.clientX - rect.left) / rect.width) * (t1 - t0);
        let best = 0;
        points.forEach((p, i) => { if (Math.abs(Date.parse(p.ts) - tx) < Math.abs(Date.parse(points[best].ts) - tx)) best = i; });
        setHover(best);
      }}
      onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="size-full" preserveAspectRatio="none" aria-hidden>
        {points.filter((p) => (p.loss ?? 0) > 0).map((p) => {
          const h = Math.max(4, ((p.loss ?? 0) / 100) * H);
          return <rect key={p.ts} x={x(p.ts) - 0.9} y={H - h} width={1.8} height={h} className="fill-signal-crit/80" />;
        })}
        {points.filter((p) => p.error).map((p) => <rect key={p.ts} x={x(p.ts) - 0.75} y={H - 4} width={1.5} height={4} className="fill-slate-600" />)}
        {segs.map((sg, i) => <path key={i} d={sg.d} fill="none" stroke={LINE[sg.i % LINE.length]} strokeWidth={1.2} vectorEffect="non-scaling-stroke" />)}
        {hp && <line x1={x(hp.ts)} x2={x(hp.ts)} y1={0} y2={H} className="stroke-slate-400/60" strokeWidth={1} vectorEffect="non-scaling-stroke" />}
      </svg>
      {hp && (
        <span className="pointer-events-none absolute top-full right-0 z-40 mt-1 w-56 rounded-md border border-rule bg-[#070b14] p-2 text-[11.5px] shadow-2xl">
          <span className="data block text-slate-400">{new Date(hp.ts).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })} ·{" "}
            <span className="text-slate-100">{hp.error ? t("bras.why.error") : hp.avg === null ? t("bras.st.unreachable") : msText(hp.avg)}</span>
            {!hp.error && (hp.loss ?? 0) > 0 && <span className="text-signal-crit"> · {lossText(hp.loss ?? 0)} {t("bras.lossUp")}</span>}
          </span>
          {hp.ip && <span className="data block text-slate-500">{t("bras.server")} {hp.ip}{ips.length > 1 && <span style={{ color: LINE[ips.indexOf(hp.ip) % LINE.length] }}> ●</span>}</span>}
          <span className="mt-1 grid grid-cols-2 gap-x-3 text-slate-500">
            <span>{t("bras.tt.avg")} <b className="data font-normal text-slate-200">{msText(s.avg)}</b></span>
            <span>{t("bras.tt.loss")} <b className={cn("data font-normal", s.loss ? "text-signal-warn" : "text-slate-200")}>{s.loss === null ? "—" : lossText(s.loss)}</b></span>
            <span>Min <b className="data font-normal text-slate-200">{msText(s.min)}</b></span>
            <span>Max <b className="data font-normal text-slate-200">{msText(s.max)}</b></span>
            <span className="col-span-2">{t("bras.tt.period", { p: period, n: s.n, ev: s.lossEvents })}</span>
          </span>
        </span>
      )}
    </span>
  );
}

function MiniLine({ points, className, tone }: { points: { ts: string; v: number }[]; className?: string; tone: Tone }) {
  if (points.length < 2) return <span className={cn("block", className)} />;
  const W = 200, H = 40;
  const t0 = Date.parse(points[0].ts), t1 = Math.max(t0 + 1, Date.parse(points[points.length - 1].ts));
  const lo = Math.min(...points.map((p) => p.v)), hi = Math.max(...points.map((p) => p.v));
  const y = (v: number) => H - 2 - ((v - lo) / Math.max(1, hi - lo)) * (H - 4);
  const dd = points.map((p, i) => `${i ? "L" : "M"}${(((Date.parse(p.ts) - t0) / (t1 - t0)) * W).toFixed(1)},${y(p.v).toFixed(1)}`).join("");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={cn("w-full", className)} preserveAspectRatio="none" aria-hidden>
      <path d={dd} className={cn("fill-none", tone === "crit" ? "stroke-signal-crit" : "stroke-signal")} strokeWidth={1.3} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ---------- drawers ----------

function DrawerShell({ title, sub, onClose, children }: { title: ReactNode; sub?: ReactNode; onClose: () => void; children: ReactNode }) {
  const { t } = useI18n();
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside role="dialog" onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-[680px] flex-col overflow-y-auto border-l border-rule bg-[#080d17] shadow-2xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-rule bg-[#080d17] px-5 py-4">
          <div className="min-w-0">{title}{sub && <div className="mt-0.5 text-[12px] text-slate-500">{sub}</div>}</div>
          <button type="button" aria-label={t("common.close")} onClick={onClose} className="grid size-8 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100"><FiX /></button>
        </header>
        <div className="space-y-4 px-5 py-4">{children}</div>
      </aside>
    </div>
  );
}

const WINDOWS = ["1h", "6h", "24h", "7d", "30d"] as const;

function TargetDrawer({ d, id, canTest, onClose, onRun }: {
  d: BRASOverview; id: number; canTest: boolean; onClose: () => void; onRun: (router: number, target: string, run: "ping" | "mtr") => void;
}) {
  const { t } = useI18n();
  const tg = d.targets.find((x) => x.id === id);
  const [win, setWin] = useState<(typeof WINDOWS)[number]>("6h");
  const hist = useQuery({ queryKey: ["bras-target", id, win], queryFn: () => targetHistory(id, win), enabled: !!tg });
  if (!tg) return null;
  const routers = d.routers.filter((r) => r.enabled && probes(tg, r.id));
  const cells = routers.map((r) => ({ router: r, pair: d.analysis.pairs[key(r.id, tg.id)] }));
  const sp = spread(cells, d.policy);
  const worst = cells.reduce<PairState>((w, c) => (c.pair && STATE_RANK[c.pair.state] < STATE_RANK[w] ? c.pair.state : w), "healthy");
  const finding = (d.analysis.findings ?? []).find((f) => f.target_ids.includes(id));
  const bad = cells.filter((c) => c.pair && c.pair.state !== "healthy" && c.pair.state !== "unknown").map((c) => c.router.name);
  return (
    <DrawerShell onClose={onClose}
      title={<><p className="eyebrow">{t("bras.target")}{tg.category && ` · ${tg.category}`}</p><h2 className="text-lg font-semibold text-slate-100">{tg.name}</h2></>}
      sub={<span className="data">{tg.address}</span>}>
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={STATE_UI[worst].tone} icon={STATE_UI[worst].icon}>
          {worst === "healthy" ? t("bras.st.healthy") : bad.length ? t("bras.stateVia", { state: t(`bras.st.${worst}` as Key), bras: bad.join(", ") }) : t(`bras.st.${worst}` as Key)}
        </Badge>
        {finding && <span className="text-[12px] text-slate-400">{caseText(finding, d, t).title}</span>}
      </div>
      <div className={cn("grid gap-3", cells.length > 1 ? "grid-cols-2" : "grid-cols-1")}>
        {cells.map(({ router, pair }, i) => (
          <section key={router.id} className="rounded-md border border-rule p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-2 truncate text-[13px] font-semibold text-slate-100"><span className="size-2 rounded-full" style={{ background: LINE[i % LINE.length] }} />{router.name}</p>
              <Badge tone={STATE_UI[pair?.state ?? "unknown"].tone}>{t(`bras.st.${pair?.state ?? "unknown"}` as Key)}</Badge>
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2 text-[12px]">
              <Stat label={t("bras.d.now")} v={msText(pair?.median_ms)} />
              <Stat label={t("bras.d.typical")} v={msText(pair?.baseline_ms)} />
              <Stat label={t("bras.d.jitter")} v={msText(pair?.jitter_ms)} />
              <Stat label={t("bras.d.lossNow")} v={pair?.loss_pct === null || pair?.loss_pct === undefined ? "—" : lossText(pair.loss_pct)} tone={(pair?.loss_pct ?? 0) > 0 ? "crit" : undefined} />
              <Stat label={t("bras.d.loss3h")} v={pair?.window_loss === null || pair?.window_loss === undefined ? "—" : lossText(pair.window_loss)} tone={(pair?.window_loss ?? 0) > 0 ? "warn" : undefined} />
              <Stat label={t("bras.d.avg3h")} v={msText(pair?.window_avg)} />
            </dl>
            {(pair?.ips?.length ?? 0) > 0 && (
              <div className="mt-2 border-t border-rule/60 pt-2">
                <p className="text-[11px] text-slate-500">{t("bras.servers3h")}</p>
                <ul className="mt-1 space-y-0.5 text-[11.5px]">
                  {pair!.ips!.map((x, k) => (
                    <li key={x.ip} className="flex items-baseline gap-2">
                      {pair!.ips!.length > 1 && <span className="size-1.5 shrink-0 rounded-full" style={{ background: LINE[k % LINE.length] }} />}
                      <span className={cn("data", x.ip === pair!.resolved_ip ? "text-slate-100" : "text-slate-400")}>{x.ip}</span>
                      <span className="min-w-0 flex-1 truncate text-slate-500" title={x.name}>{x.name || t("bras.noPtr")}</span>
                      <span className="data text-slate-300">{msText(x.median_ms)}</span>
                      <span className="data w-9 text-right text-slate-500">{Math.round(x.share * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {canTest && (
              <div className="mt-2.5 flex gap-2 border-t border-rule/60 pt-2">
                <Button size="xs" variant="outline" onClick={() => onRun(router.id, tg.address, "ping")}><FiActivity /> {t("bras.ping")}</Button>
                <Button size="xs" variant="outline" onClick={() => onRun(router.id, tg.address, "mtr")}><FiClock /> MTR</Button>
              </div>
            )}
          </section>
        ))}
      </div>
      {sp && (
        <p className={cn("rounded-sm border px-3 py-2 text-[12.5px]", sp.differentServers && sp.delta >= d.policy.dev_min_ms ? "border-signal/30 bg-signal/[0.05] text-slate-200" : sp.meaningful ? "border-amber-400/30 bg-amber-400/[0.05] text-amber-100" : "border-rule text-slate-400")}>
          {sp.differentServers && sp.delta >= d.policy.dev_min_ms
            ? t("bras.cmpServers", { slow: sp.slow.name, fast: sp.fast.name, d: sp.delta.toFixed(1) })
            : sp.meaningful ? t("bras.cmpSlower", { bras: sp.slow.name, d: sp.delta.toFixed(1), fast: sp.fast.name }) : t("bras.cmpSame", { d: sp.delta.toFixed(1) })}
        </p>
      )}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <p className="eyebrow">{t("bras.history")}</p>
          <div className="flex gap-1">
            {WINDOWS.map((w) => (
              <button key={w} type="button" aria-pressed={win === w} onClick={() => setWin(w)}
                className={cn("rounded-sm border px-2 py-0.5 text-[11.5px]", win === w ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400")}>{w}</button>
            ))}
          </div>
        </div>
        <CompareChart samples={hist.data?.samples ?? []} routers={routers} loading={hist.isLoading} />
      </section>
    </DrawerShell>
  );
}

const Stat = ({ label, v, tone }: { label: string; v: string; tone?: Tone }) => (
  <div className="min-w-0"><dt className="text-[11px] text-slate-500">{label}</dt><dd className={cn("data mt-0.5 truncate text-[13px]", tone ? TONE[tone].text : "text-slate-100")}>{v}</dd></div>
);

/** One latency line per BRAS on a shared scale, loss as bars in a lane per BRAS. */
function CompareChart({ samples, routers, loading }: {
  samples: { ts: string; router_id: number; rtt_avg: number | null; loss_pct: number | null; error: string | null }[]; routers: BRASRouter[]; loading: boolean;
}) {
  const { t, locale } = useI18n();
  const W = 640, H = 170, LOSS_H = 22;
  if (loading) return <p className="py-10 text-center text-[12px] text-slate-500">{t("common.loading")}</p>;
  if (samples.length < 2) return <p className="py-10 text-center text-[12px] text-slate-500">{t("bras.noHistory")}</p>;
  const ts = samples.map((s) => Date.parse(s.ts));
  const t0 = Math.min(...ts), t1 = Math.max(t0 + 1, ...ts);
  const vals = samples.map((s) => s.rtt_avg).filter((v): v is number => v !== null);
  const top = (vals.length ? Math.max(...vals) : 1) * 1.1;
  const x = (iso: string) => ((Date.parse(iso) - t0) / (t1 - t0)) * W;
  const y = (v: number) => H - LOSS_H - 4 - (v / top) * (H - LOSS_H - 12);
  return (
    <div className="rounded-sm border border-rule p-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-44 w-full" preserveAspectRatio="none">
        {[0.25, 0.5, 0.75, 1].map((f) => <line key={f} x1={0} x2={W} y1={y(top * f)} y2={y(top * f)} className="stroke-slate-800" strokeWidth={1} vectorEffect="non-scaling-stroke" />)}
        {routers.map((r, i) => {
          const pts = samples.filter((s) => s.router_id === r.id);
          let dd = "", prev = false;
          for (const p of pts) {
            if (p.rtt_avg === null) { prev = false; continue; }
            dd += `${prev ? "L" : "M"}${x(p.ts).toFixed(1)},${y(p.rtt_avg).toFixed(1)}`;
            prev = true;
          }
          const laneH = LOSS_H / Math.max(1, routers.length);
          const lane = H - LOSS_H + i * laneH;
          return (
            <g key={r.id}>
              <path d={dd} fill="none" stroke={LINE[i % LINE.length]} strokeWidth={1.3} vectorEffect="non-scaling-stroke" />
              {pts.filter((p) => (p.loss_pct ?? 0) > 0).map((p) => <rect key={p.ts} x={x(p.ts) - 1} y={lane} width={2} height={laneH - 1} fill="#ef4444" opacity={0.3 + ((p.loss_pct ?? 0) / 100) * 0.7} />)}
            </g>
          );
        })}
      </svg>
      <div className="data flex justify-between text-[10.5px] text-slate-500">
        <span>{fmtDateTime(locale, new Date(t0).toISOString())}</span><span>{t("bras.chartMax", { v: msText(top / 1.1) })}</span><span>{fmtDateTime(locale, new Date(t1).toISOString())}</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">{t("bras.chartLegend2")}</p>
    </div>
  );
}

function RouterDrawer({ d, id, now, canTest, onClose, onRun, onTarget }: {
  d: BRASOverview; id: number; now: number; canTest: boolean; onClose: () => void; onRun: (router: number) => void; onTarget: (id: number) => void;
}) {
  const { t, locale } = useI18n();
  const r = d.routers.find((x) => x.id === id);
  const [win, setWin] = useState<"24h" | "7d">("24h");
  const hist = useQuery({ queryKey: ["bras-router", id, win], queryFn: () => routerHistory(id, win), enabled: !!r });
  const test = useMutation({ mutationFn: () => testRouter(id) });
  if (!r) return null;
  const net = routerInternet(d, r);
  const mem = r.mem_used_b !== null && r.mem_total_b ? (r.mem_used_b / r.mem_total_b) * 100 : null;
  const tone: Tone = !r.enabled ? "muted" : r.status === "down" ? "crit" : r.status === "up" ? "good" : "muted";
  const targets = d.targets.filter((tg) => tg.enabled && probes(tg, r.id))
    .map((tg) => ({ tg, pair: d.analysis.pairs[key(r.id, tg.id)] }))
    .sort((a, b) => STATE_RANK[a.pair?.state ?? "unknown"] - STATE_RANK[b.pair?.state ?? "unknown"]);
  const transport = r.kind === "routeros" && r.api_port ? `API :${r.api_port}${r.api_tls ? " TLS" : ""}` : `SSH :${r.port}`;
  return (
    <DrawerShell onClose={onClose}
      title={<><p className="eyebrow">BRAS</p><h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100"><Dot tone={tone} /> {r.name}</h2></>}
      sub={<span className="data">{[r.host, transport, r.board, r.version, r.identity].filter(Boolean).join(" · ")}</span>}>
      {r.status === "down" && <p className="rounded-sm border border-signal-crit/40 bg-signal-crit/[0.06] px-3 py-2 text-[12.5px] text-rose-200"><b>{t("bras.unreachable")}.</b> {r.last_error} · {t("bras.lastOk", { age: ago(r.last_ok_at, now) })}</p>}
      <section className="rounded-md border border-rule p-3">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] text-slate-500">{t("bras.pppoeSessions")}</p>
            <p className="data text-[26px] leading-none font-semibold text-slate-100">{r.pppoe_sessions === null ? "—" : fmtNum(locale, r.pppoe_sessions)}</p>
          </div>
          <div className="flex gap-1">
            {(["24h", "7d"] as const).map((w) => (
              <button key={w} type="button" aria-pressed={win === w} onClick={() => setWin(w)}
                className={cn("rounded-sm border px-2 py-0.5 text-[11.5px]", win === w ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400")}>{w}</button>
            ))}
          </div>
        </div>
        <MiniLine points={(hist.data?.samples ?? []).filter((s) => s.pppoe !== null).map((s) => ({ ts: s.ts, v: s.pppoe! }))} className="mt-2 h-20" tone="info" />
        <dl className="mt-2 grid grid-cols-3 gap-3 border-t border-rule/60 pt-2">
          <Stat label={r.kind === "linux" ? t("bras.load") : "CPU"} v={r.cpu_pct === null ? "—" : `${r.cpu_pct.toFixed(0)}%`} tone={(r.cpu_pct ?? 0) >= 85 ? "crit" : (r.cpu_pct ?? 0) >= 70 ? "warn" : undefined} />
          <Stat label={t("bras.memory")} v={mem === null ? "—" : `${mem.toFixed(0)}%`} tone={(mem ?? 0) >= 90 ? "crit" : undefined} />
          <Stat label={t("bras.uptime")} v={uptimeText(r.uptime_s)} />
        </dl>
      </section>
      <section>
        <p className="eyebrow mb-2">{t("bras.internetHealth")}</p>
        <dl className="grid grid-cols-3 gap-3 rounded-md border border-rule p-3">
          <Stat label={t("bras.f.targets")} v={`${net.healthy}/${net.total}`} tone={net.healthy < net.total ? "warn" : "good"} />
          <Stat label={t("bras.f.median")} v={msText(net.medianMs)} />
          <Stat label={t("bras.d.loss3h")} v={net.loss === null ? "—" : lossText(net.loss)} tone={(net.loss ?? 0) > 0 ? "warn" : undefined} />
        </dl>
        <ul className="mt-2 divide-y divide-rule/60 rounded-md border border-rule">
          {targets.map(({ tg, pair }) => (
            <li key={tg.id}>
              <button type="button" onClick={() => onTarget(tg.id)} className="flex w-full items-center gap-3 px-3 py-1.5 text-left text-[12.5px] hover:bg-white/[0.02]">
                <Dot tone={STATE_UI[pair?.state ?? "unknown"].tone} />
                <span className="flex-1 truncate text-slate-200">{tg.name}</span>
                <span className="data text-slate-300">{msText(pair?.median_ms)}</span>
                <span className={cn("data w-16 text-right", (pair?.window_loss ?? 0) > 0 ? "text-signal-warn" : "text-slate-500")}>{pair?.window_loss === null || pair?.window_loss === undefined ? "—" : lossText(pair.window_loss)}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
      {(test.data || test.error) && <p className={cn("text-[12px]", test.error ? "text-signal-crit" : "text-signal-good")}>{test.error ? (test.error as Error).message : t("bras.testOk", { id: test.data!.identity || "—" })}</p>}
      {canTest && (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onRun(r.id)}><FiActivity /> {t("bras.runDiag")}</Button>
          <Button size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}><FiZap /> {t("bras.test")}</Button>
        </div>
      )}
    </DrawerShell>
  );
}

const ERR_KEY: Record<NonNullable<ProbeTrace["error_kind"]>, Key> = {
  api: "bras.ek.api", timeout: "bras.ek.timeout", unreachable: "bras.ek.unreachable", incomplete: "bras.ek.incomplete",
};

function ResultDrawer({ tr, router, onClose }: { tr: ProbeTrace; router: string; onClose: () => void }) {
  const { t, locale } = useI18n();
  const [raw, setRaw] = useState(false);
  return (
    <DrawerShell onClose={onClose}
      title={<><p className="eyebrow">{tr.kind === "ping" ? t("bras.pingResult") : t("bras.traceResult")}</p><h2 className="text-lg font-semibold text-slate-100">{router} → <span className="data">{tr.target}</span></h2></>}
      sub={`${fmtDateTime(locale, tr.ts)} · ${t(tr.source === "auto" ? "bras.autoTraceLong" : "bras.manualTrace")}`}>
      <ResultBadge tr={tr} />
      {tr.error_kind && <p className="text-[12.5px] text-slate-300">{t(ERR_KEY[tr.error_kind])}</p>}
      {tr.kind === "ping" && tr.result && <PingStats r={tr.result} />}
      {tr.kind === "mtr" && tr.hops.length > 0 && <HopTable hops={tr.hops} />}
      {tr.error && (
        <div>
          <button type="button" onClick={() => setRaw(!raw)} className="text-[12px] text-slate-400 hover:text-signal">{t(raw ? "bras.hideRaw" : "bras.viewRaw")}</button>
          {raw && <pre className="mt-1 overflow-x-auto rounded-sm border border-rule bg-black/30 p-2 text-[11.5px] text-slate-300">{tr.error}</pre>}
        </div>
      )}
    </DrawerShell>
  );
}

function ResultBadge({ tr }: { tr: ProbeTrace }) {
  const { t } = useI18n();
  if (tr.error_kind === "api" || tr.error_kind === "timeout") return <Badge tone="muted" icon={FiMinusCircle}>{t(tr.error_kind === "api" ? "bras.res.toolFail" : "bras.res.timeout")}</Badge>;
  if (tr.error_kind === "unreachable") return <Badge tone="crit" icon={FiXCircle}>{t("bras.st.unreachable")}</Badge>;
  if (tr.error_kind === "incomplete") return <Badge tone="warn" icon={FiAlertTriangle}>{t("bras.res.incomplete")}</Badge>;
  if (tr.error) return <Badge tone="muted" icon={FiMinusCircle}>{t("bras.res.toolFail")}</Badge>;
  if (tr.kind === "ping" && tr.result && tr.result.loss_pct > 0) return <Badge tone="warn" icon={FiAlertTriangle}>{lossText(tr.result.loss_pct)} {t("bras.lossUp")}</Badge>;
  return <Badge tone="good" icon={FiCheckCircle}>{t("bras.res.ok")}</Badge>;
}

function resultSummary(tr: ProbeTrace, t: T): string {
  if (tr.error_kind === "api" || (tr.error && !tr.error_kind)) return t("bras.res.toolFailS");
  if (tr.error_kind === "timeout") return t("bras.res.timeout");
  if (tr.kind === "ping") return tr.result ? `${lossText(tr.result.loss_pct)} ${t("bras.lossUp")} · ${msText(tr.result.avg_ms)}` : "—";
  const last = tr.hops[tr.hops.length - 1];
  return `${t("bras.hops", { n: tr.hops.length })}${last?.avg_ms !== null && last?.avg_ms !== undefined ? ` · ${msText(last.avg_ms)}` : ""}`;
}

/**
 * Loss on an intermediate hop that later hops do not share is ICMP rate
 * limiting, not a fault: shown muted. Loss that persists to the end is
 * highlighted from the hop where it begins.
 */
function HopTable({ hops }: { hops: Hop[] }) {
  const { t } = useI18n();
  const endLoss = hops.length ? hops[hops.length - 1].loss_pct : 0;
  let start = -1;
  if (endLoss > 0) {
    start = hops.length - 1;
    while (start > 0 && hops[start - 1].loss_pct > 0) start--;
  }
  return (
    <table className="w-full text-[12.5px]">
      <thead>
        <tr className="border-b border-rule text-left text-[11px] text-slate-500">
          <th className="w-8 py-1 font-normal">#</th><th className="py-1 font-normal">{t("bras.hop")}</th>
          <th className="py-1 text-right font-normal">{t("bras.loss")}</th><th className="py-1 text-right font-normal">Avg</th>
          <th className="py-1 text-right font-normal">Best</th><th className="py-1 text-right font-normal">Worst</th>
        </tr>
      </thead>
      <tbody className="data">
        {hops.map((h, i) => {
          const persistent = start >= 0 && i >= start;
          const limited = h.loss_pct > 0 && !persistent;
          return (
            <tr key={h.n} className={cn("border-b border-rule/40 last:border-b-0", persistent && "bg-signal-crit/[0.05]")}>
              <td className="py-1 text-slate-500">{h.n}</td>
              <td className="py-1 text-slate-100">{h.address || <span className="text-slate-500">{t("bras.noReply")}</span>}
                {limited && <span className="ml-2 font-sans text-[11px] text-slate-500">{t("bras.rateLimited")}</span>}</td>
              <td className={cn("py-1 text-right", persistent ? "font-semibold text-signal-crit" : limited ? "text-slate-500" : "text-slate-300")}>{h.loss_pct.toFixed(0)}%</td>
              <td className="py-1 text-right text-slate-300">{msText(h.avg_ms)}</td>
              <td className="py-1 text-right text-slate-400">{msText(h.best_ms)}</td>
              <td className="py-1 text-right text-slate-400">{msText(h.worst_ms)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------- diagnostics ----------

function Tools({ d, preset, onResult }: { d: BRASOverview; preset: { router?: number; target?: string; run?: "ping" | "mtr"; nonce: number }; onResult: (tr: ProbeTrace) => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const usable = d.routers.filter((r) => r.enabled);
  const [rid, setRid] = useState<number>(usable[0]?.id ?? 0);
  const [target, setTarget] = useState("");
  const [count, setCount] = useState(10);
  const [last, setLast] = useState<{ router: string; target: string; result: PingResult } | null>(null);
  const ping = useMutation({
    mutationFn: (x: { rid: number; target: string }) => routerPing(x.rid, x.target, count),
    onSuccess: (res) => { setLast(res); qc.invalidateQueries({ queryKey: ["bras"] }); },
    onError: () => qc.invalidateQueries({ queryKey: ["bras"] }),
  });
  const trace = useMutation({
    mutationFn: (x: { rid: number; target: string }) => routerTrace(x.rid, x.target),
    onSuccess: (tr) => { qc.invalidateQueries({ queryKey: ["bras"] }); onResult(tr); },
  });
  // A preset from a drawer or an incident fills the form and runs it.
  useEffect(() => {
    if (!preset.nonce) return;
    if (preset.router) setRid(preset.router);
    if (preset.target !== undefined) setTarget(preset.target);
    if (preset.router && preset.target) {
      const x = { rid: preset.router, target: preset.target };
      if (preset.run === "mtr") trace.mutate(x); else ping.mutate(x);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.nonce]);
  const valid = /^[A-Za-z0-9.:-]{2,253}$/.test(target.trim());
  const run = (kind: "ping" | "mtr") => (kind === "ping" ? ping : trace).mutate({ rid, target: target.trim() });
  return (
    <Panel title={t("bras.diagnostics")} right={<span className="text-[11px] text-slate-500">{t("bras.manualNote")}</span>}>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_auto] items-end gap-2">
        <label className="text-[11.5px] text-slate-400">{t("bras.from")}
          <select value={rid} onChange={(e) => setRid(Number(e.target.value))} className="mt-1 block h-8 w-full rounded-sm border border-rule bg-panel px-2 text-[12.5px] text-slate-100">
            {usable.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <label className="text-[11.5px] text-slate-400">{t("bras.targetAddr")}
          <Input list="bras-targets" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="facebook.com, 8.8.8.8, 100.64.3.9"
            className="mt-1 h-8 font-mono text-[12.5px]" onKeyDown={(e) => { if (e.key === "Enter" && valid) run("ping"); }} />
          <datalist id="bras-targets">{d.targets.map((x) => <option key={x.id} value={x.address}>{x.name}</option>)}</datalist>
        </label>
        <label className="text-[11.5px] text-slate-400">{t("bras.count")}
          <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="mt-1 block h-8 rounded-sm border border-rule bg-panel px-2 text-[12.5px] text-slate-100">
            {[5, 10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" disabled={!valid || !rid || ping.isPending} onClick={() => run("ping")}><FiActivity /> {t(ping.isPending ? "bras.pinging" : "bras.ping")}</Button>
        <Button size="sm" variant="outline" disabled={!valid || !rid || trace.isPending} onClick={() => run("mtr")}><FiClock /> {t(trace.isPending ? "bras.tracing" : "bras.trace")}</Button>
        {trace.isPending && <span className="self-center text-[11.5px] text-slate-500">{t("bras.traceWait")}</span>}
      </div>
      {(ping.error || trace.error) && <p className="mt-3 text-[12px] text-signal-crit">{t("bras.ek.api")} {((ping.error || trace.error) as Error).message}</p>}
      {last && !ping.isPending && (
        <div className="mt-3 border-t border-rule/60 pt-3">
          <p className="mb-2 flex items-center gap-2 text-[12px] text-slate-400">
            {last.result.received === 0 ? <Badge tone="crit">{t("bras.st.unreachable")}</Badge> : <Badge tone={last.result.loss_pct > 0 ? "warn" : "good"}>{t("bras.res.reachable")}</Badge>}
            {last.router} → <span className="data text-slate-200">{last.target}</span>
          </p>
          <PingStats r={last.result} />
          {last.result.min_ms !== null && last.result.max_ms !== null && <p className="mt-1 text-[11.5px] text-slate-500">{t("bras.spreadMs", { v: msText(last.result.max_ms - last.result.min_ms) })}</p>}
        </div>
      )}
    </Panel>
  );
}

function RecentDiagnostics({ d, now, locale, routerName, onOpen }: { d: BRASOverview; now: number; locale: string; routerName: (id: number) => string; onOpen: (tr: ProbeTrace) => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<"manual" | "auto">("manual");
  const rows = tab === "manual" ? d.diagnostics : d.auto_traces;
  return (
    <Panel pad={false} title={t("bras.recentDiag")} right={
      <div role="tablist" className="flex rounded-sm border border-rule p-0.5">
        {(["manual", "auto"] as const).map((x) => (
          <button key={x} role="tab" type="button" aria-selected={tab === x} onClick={() => setTab(x)}
            className={cn("rounded-sm px-2 py-0.5 text-[11.5px]", tab === x ? "bg-signal/10 text-slate-100" : "text-slate-400")}>{t(`bras.tab.${x}` as Key)}</button>
        ))}
      </div>
    }>
      {rows.length === 0 ? <p className="px-4 py-4 text-[12.5px] text-slate-500">{t(tab === "manual" ? "bras.noManual" : "bras.noAuto")}</p> : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-[12.5px]">
            <thead className="sticky top-0 bg-panel">
              <tr className="border-b border-rule text-left">
                {(["bras.col.time", "bras.col.source", "bras.col.target", "bras.col.type", "bras.col.result", "bras.col.summary"] as Key[]).map((k) => (
                  <th key={k} className="eyebrow px-3 py-1.5 font-medium">{t(k)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((tr) => (
                <tr key={tr.id} onClick={() => onOpen(tr)} className="cursor-pointer border-b border-rule/50 last:border-b-0 hover:bg-white/[0.02]">
                  <td className="data px-3 py-1.5 text-[11.5px] text-slate-500" title={fmtDateTime(locale, tr.ts)}>{t("ua.ago", { age: ago(tr.ts, now) })}</td>
                  <td className="px-3 py-1.5 text-slate-300">{routerName(tr.router_id)}</td>
                  <td className="data px-3 py-1.5 text-slate-100">{tr.target}</td>
                  <td className="px-3 py-1.5 text-slate-400">{tr.kind === "ping" ? "Ping" : "MTR"}</td>
                  <td className="px-3 py-1.5"><ResultBadge tr={tr} /></td>
                  <td className="data px-3 py-1.5 text-[11.5px] text-slate-400">{resultSummary(tr, t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ---------- setup, menus, dialogs ----------

function SetupHelp({ admin, onAdd }: { admin: boolean; onAdd: () => void }) {
  const { t } = useI18n();
  return (
    <Panel title={t("bras.setup")}>
      <p className="max-w-3xl text-[12.5px] text-slate-300">{t("bras.setupBody")}</p>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div>
          <p className="eyebrow mb-1">RouterOS</p>
          <pre className="overflow-x-auto rounded-sm border border-rule bg-black/30 p-2 text-[11.5px] text-slate-300">{`/ip service enable api
/ip service set api address=<panel server IP>
/user group add name=nocops policy=api,read,test
/user add name=nocops group=nocops password=<strong>`}</pre>
        </div>
        <div>
          <p className="eyebrow mb-1">Linux</p>
          <pre className="overflow-x-auto rounded-sm border border-rule bg-black/30 p-2 text-[11.5px] text-slate-300">{`useradd -m -s /bin/bash nocops && passwd nocops
apt install mtr-tiny   # optional, for MTR`}</pre>
        </div>
      </div>
      <p className="mt-2 text-[11.5px] text-slate-500">{t("bras.setupNote")}</p>
      {admin && <Button size="sm" className="mt-3" onClick={onAdd}><FiPlus /> {t("bras.addRouter")}</Button>}
    </Panel>
  );
}

type MenuItem = { icon: IconType; label: string; onClick: () => void; danger?: boolean };
function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)}
        className="grid size-7 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100"><FiMoreHorizontal /></button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 text-left shadow-2xl">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem" onClick={() => { setOpen(false); it.onClick(); }}
              className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px]", it.danger ? "text-rose-300 hover:bg-signal-crit/10" : "text-slate-200 hover:bg-white/[0.05]")}>
              <it.icon className="size-3.5" /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RouterDialog({ edit, onClose }: { edit: BRASRouter | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [f, setF] = useState<RouterForm>(edit
    ? { name: edit.name, kind: edit.kind, host: edit.host, port: edit.port, api_port: edit.api_port, api_tls: edit.api_tls, username: edit.username ?? "", password: "", notes: edit.notes ?? "" }
    : { name: "", kind: "routeros", host: "", port: 22, api_port: 8728, api_tls: false, username: "", password: "" });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, password: f.password || undefined };
      return edit ? updateRouter(edit.id, body) : createRouter(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bras"] }); onClose(); },
  });
  const field = (k: keyof RouterForm, label: string, type = "text") => (
    <label className="block text-xs text-slate-400">{label}
      <Input type={type} autoComplete={type === "password" ? "new-password" : "off"} value={String(f[k] ?? "")}
        onChange={(e) => setF({ ...f, [k]: k === "port" ? Number(e.target.value) || undefined : e.target.value })} />
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{edit ? t("bras.editRouter", { name: edit.name }) : t("bras.addRouter")}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {field("name", t("common.name"))}
          <label className="block text-xs text-slate-400">{t("bras.kind")}
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as RouterForm["kind"] })} className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm">
              <option value="routeros">MikroTik RouterOS</option>
              <option value="linux">Linux (accel-ppp)</option>
            </select>
          </label>
          {field("host", t("bras.host"))}
          {f.kind === "routeros" ? (
            <label className="block text-xs text-slate-400">{t("bras.apiPort")}
              <Input type="number" value={String(f.api_port ?? 0)} onChange={(e) => setF({ ...f, api_port: Number(e.target.value) || 0 })} />
              <span className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                <input type="checkbox" checked={!!f.api_tls} onChange={(e) => setF({ ...f, api_tls: e.target.checked, api_port: e.target.checked && f.api_port === 8728 ? 8729 : !e.target.checked && f.api_port === 8729 ? 8728 : f.api_port })} /> {t("bras.apiTls")}
              </span>
            </label>
          ) : field("port", t("bras.sshPort"), "number")}
          {field("username", t("bras.username"))}
          {field("password", t(edit ? "bras.passwordKeep" : "bras.password"), "password")}
        </div>
        <p className="text-[11px] text-slate-500">{t("bras.credNote")}</p>
        {save.error && <p className="text-sm text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending || !f.name || !f.host || !f.username || (!edit && !f.password)} onClick={() => save.mutate()}>{t(save.isPending ? "common.saving" : "common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CATEGORIES = ["DNS", "CDN", "Social / Content", "Gaming", "Local / Speedtest", "IXP", "Upstream", "International"];

function TargetDialog({ edit, routers, targets, onClose }: { edit: ProbeTarget | null; routers: BRASRouter[]; targets: ProbeTarget[]; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [f, setF] = useState<TargetForm>({
    name: edit?.name ?? "", address: edit?.address ?? "", category: edit?.category ?? "",
    router_ids: edit?.router_ids ?? [], latency_warn_ms: edit?.latency_warn_ms ?? undefined, loss_warn_pct: edit?.loss_warn_pct ?? undefined,
  });
  const allRouters = !f.router_ids?.length;
  const save = useMutation({
    mutationFn: () => saveProbeTarget({
      ...f, name: f.name?.trim(), address: f.address?.trim(),
      // Both override fields emptied: judge the target by the policy again.
      clear_thresholds: f.latency_warn_ms === undefined && f.loss_warn_pct === undefined,
    }, edit?.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["bras"] }); onClose(); },
  });
  const cats = [...new Set([...CATEGORIES, ...targets.map((x) => x.category).filter(Boolean)])];
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{edit ? t("bras.editTarget") : t("bras.addTarget")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("bras.targetHint")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs text-slate-400">{t("common.name")}<Input value={f.name} placeholder="Cloudflare DNS" onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
          <label className="block text-xs text-slate-400">{t("bras.targetAddr")}<Input value={f.address} placeholder="1.1.1.1" className="font-mono" onChange={(e) => setF({ ...f, address: e.target.value })} /></label>
          <label className="block text-xs text-slate-400">{t("bras.category")}
            <Input list="bras-cats" value={f.category} placeholder="DNS" onChange={(e) => setF({ ...f, category: e.target.value })} />
            <datalist id="bras-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
          </label>
          <label className="block text-xs text-slate-400">{t("bras.probeType")}<Input value="ICMP" disabled /></label>
        </div>
        <div>
          <p className="text-xs text-slate-400">{t("bras.probedFrom")}</p>
          <div className="mt-1 flex flex-wrap gap-3 text-[12.5px] text-slate-200">
            {routers.map((r) => (
              <label key={r.id} className="flex items-center gap-1.5">
                <input type="checkbox" checked={allRouters || !!f.router_ids?.includes(r.id)}
                  onChange={(e) => {
                    const cur = allRouters ? routers.map((x) => x.id) : f.router_ids ?? [];
                    const next = e.target.checked ? [...cur, r.id] : cur.filter((x) => x !== r.id);
                    setF({ ...f, router_ids: next.length === routers.length ? [] : next });
                  }} /> {r.name}
              </label>
            ))}
          </div>
        </div>
        <div className="rounded-sm border border-rule p-3">
          <p className="text-xs text-slate-400">{t("bras.overrides")}</p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-slate-400">{t("bras.latWarn")}<Input type="number" value={f.latency_warn_ms ?? ""} placeholder={t("bras.usePolicy")} onChange={(e) => setF({ ...f, latency_warn_ms: num(e.target.value) })} /></label>
            <label className="block text-xs text-slate-400">{t("bras.lossWarn")}<Input type="number" value={f.loss_warn_pct ?? ""} placeholder={t("bras.usePolicy")} onChange={(e) => setF({ ...f, loss_warn_pct: num(e.target.value) })} /></label>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">{t("bras.overrideNote")}</p>
        </div>
        {save.error && <p className="text-sm text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending || !f.address?.trim()} onClick={() => save.mutate()}>{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDialog({ policy, onClose }: { policy: ProbePolicy; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [p, setP] = useState<ProbePolicy>(policy);
  const save = useMutation({ mutationFn: () => saveProbePolicy(p), onSuccess: () => { qc.invalidateQueries({ queryKey: ["bras"] }); onClose(); } });
  const num = (k: keyof ProbePolicy, label: string, unit: string) => (
    <label className="block text-xs text-slate-400">{label}
      <span className="mt-1 flex items-center gap-2"><Input type="number" step="0.5" value={String(p[k])} onChange={(e) => setP({ ...p, [k]: Number(e.target.value) })} className="w-24 font-mono" /><span className="text-slate-500">{unit}</span></span>
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("bras.thresholds")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("bras.policyHint")}</DialogDescription>
        </DialogHeader>
        <p className="eyebrow">{t("bras.loss")}</p>
        <div className="grid grid-cols-2 gap-3">{num("loss_warn_pct", t("bras.p.lossWarn"), "%")}{num("loss_crit_pct", t("bras.p.lossCrit"), "%")}</div>
        <p className="eyebrow">{t("bras.p.latency")}</p>
        <div className="grid grid-cols-2 gap-3">{num("dev_pct", t("bras.p.devPct"), "%")}{num("dev_min_ms", t("bras.p.devMin"), "ms")}</div>
        <p className="text-[11px] text-slate-500">{t("bras.p.latencyNote", { pct: p.dev_pct, ms: p.dev_min_ms })}</p>
        <p className="eyebrow">PPPoE</p>
        <div className="grid grid-cols-2 gap-3">{num("ppp_drop_pct", t("bras.p.pppPct"), "%")}{num("ppp_drop_min", t("bras.p.pppMin"), t("bras.p.sessions"))}</div>
        {save.error && <p className="text-sm text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRouter({ r, onClose }: { r: BRASRouter; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const del = useMutation({ mutationFn: () => deleteRouter(r.id), onSuccess: () => { qc.invalidateQueries({ queryKey: ["bras"] }); onClose(); } });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("bras.delRouterTitle", { name: r.name })}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("bras.delRouterBody")}</DialogDescription>
        </DialogHeader>
        {del.error && <p className="text-sm text-signal-crit">{(del.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="destructive" disabled={del.isPending} onClick={() => del.mutate()}>{t("common.delete")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
